import angular from 'angular';
import { TMovimentacaoQuickAddSubmitMode } from './components/quick-add/movimentacoes-quick-add.model';
import { IMovimentacaoPeriodoForm } from './models/movimentacoes-periodos-ui.model';
import { IMovimentacaoListRefreshRequest } from './components/listagem/movimentacoes-listagem.model';
import { MovimentacoesPeriodosModalService } from './modals/periodos/movimentacoes-periodos.modal.service';
import { MovimentacoesPeriodosService } from '../movimentacoes-periodos.service';
import { MovimentacoesService } from '../movimentacoes.service';
import { MovimentacoesWorkflowService } from '../movimentacoes-workflow.service';
import {
    IMovimentacaoListItem,
    IMovimentacaoPayload
} from '../models/movimentacoes.service.model';
import {
    IMovimentacaoPeriodo,
    IMovimentacaoPeriodoCreateResponse,
    IMovimentacaoPeriodoPayload,
    IMovimentacaoPeriodoUpdatePayload
} from '../models/movimentacoes-periodos.service.model';
import { extractMovimentacaoErrorMessage } from './helpers/movimentacoes-errors.helpers';
import { isMovimentacaoPeriodoEncerrado } from './helpers/movimentacoes-form.helpers';
import {
    buildSuggestedClosingDate,
    normalizePeriodoDateInput,
    resolvePeriodoSelection,
    resolveSugestaoProxPeriodo
} from './helpers/movimentacoes-periodos-ui.helpers';

export class MovimentacoesIndexController {

    static $inject = [
        '$rootScope',
        '$q',
        '$state',
        '$stateParams',
        'NewToaster',
        'MovimentacoesService',
        'MovimentacoesWorkflowService',
        'MovimentacoesPeriodosService',
        'MovimentacoesPeriodosModalService'
    ];

    public periodosBusy = false;
    public novoPeriodoBusy = false;
    public quickAddOpen = false;
    public quickAddBusy = false;
    public detalheAberto = false;
    public detalheMovimentacaoId: string | null = null;
    public periodos: IMovimentacaoPeriodo[] = [];
    public selectedPeriod: IMovimentacaoPeriodo | null = null;
    public workflowEnabled = false;
    public estabelecimento = '';
    public currentUserEmail = '';
    public periodosAnoReferencia = new Date().getFullYear();
    public listRefreshRequest: IMovimentacaoListRefreshRequest = { token: 0, preferredStatusValue: null };
    private routePeriodoId: string | null = null;
    private routeMovimentacaoId: string | null = null;

    constructor(
        public $rootScope: angular.IRootScopeService & {
            session: any;
            usuario?: { email?: string };
            temPermissao: (permissao: string | string[]) => boolean;
        },
        public $q: angular.IQService,
        public $state: angular.ui.IStateService,
        public $stateParams: angular.ui.IStateParamsService,
        public NewToaster: {
            pop: (opts: { type: string; title: string; body?: string }) => void;
        },
        public MovimentacoesService: MovimentacoesService,
        public MovimentacoesWorkflowService: MovimentacoesWorkflowService,
        public MovimentacoesPeriodosService: MovimentacoesPeriodosService,
        public MovimentacoesPeriodosModalService: MovimentacoesPeriodosModalService
    ) {
        this.estabelecimento = this.$rootScope.session?.entities?.estabelecimento?.estabelecimento ?? '';
        this.currentUserEmail = this.$rootScope.usuario?.email ?? '';
        this.routePeriodoId = this.readStateParam('periodo');
        this.routeMovimentacaoId = this.readStateParam('movimentacao');

        this.loadWorkflowContext();
        this.loadPeriodos(this.periodosAnoReferencia, this.routePeriodoId || undefined);
    }

    public selectPeriodo(periodo: IMovimentacaoPeriodo): angular.IPromise<void> {
        if (!periodo || !periodo.movimentacaoperiodo) {
            return this.$q.resolve();
        }

        this.selectedPeriod = periodo;
        this.periodosAnoReferencia = periodo.ano;
        this.closeDetalhe(false);
        this.syncRouteContext();
        return this.$q.resolve();
    }

    public buildNovoPeriodoForm(): IMovimentacaoPeriodoForm {
        const proxPeriodo = resolveSugestaoProxPeriodo(this.periodos);

        return {
            mes: proxPeriodo.mes,
            ano: proxPeriodo.ano,
            encerramentolancamentos: buildSuggestedClosingDate(proxPeriodo.ano, proxPeriodo.mes)
        };
    }

    public submitNovoPeriodo(payload: IMovimentacaoPeriodoForm): angular.IPromise<IMovimentacaoPeriodo> {
        if (!this.canManageMovimentacoes()) {
            return this.$q.reject();
        }

        if (!this.estabelecimento) {
            this.handleError({ message: 'Estabelecimento não encontrado na sessão.' }, 'Não foi possível criar o período.');
            return this.$q.reject();
        }

        const normalizedPayload = this.normalizePeriodoPayload(payload);
        if (!normalizedPayload) {
            this.handleError({ message: 'Preencha o encerramento de movimentos no formato AAAA-MM-DD.' }, 'Não foi possível criar o período.');
            return this.$q.reject();
        }

        this.novoPeriodoBusy = true;

        return this.MovimentacoesPeriodosService.create(normalizedPayload)
            .then((response) => this.handlePeriodoCreated(response, normalizedPayload.ano))
            .then((createdPeriodo) => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Período criado com sucesso.'
                });
                return createdPeriodo;
            })
            .catch((error) => {
                this.handleError(error, 'Não foi possível criar o período.');
                return this.$q.reject(error);
            })
            .finally(() => {
                this.novoPeriodoBusy = false;
            });
    }

    public submitEdicaoPeriodo(periodo: IMovimentacaoPeriodo, payload: IMovimentacaoPeriodoUpdatePayload): angular.IPromise<IMovimentacaoPeriodo> {
        if (!this.canManageMovimentacoes()) {
            return this.$q.reject();
        }

        if (!periodo || !periodo.movimentacaoperiodo) {
            return this.$q.reject({ message: 'Período inválido para edição.' });
        }

        const normalizedDate = normalizePeriodoDateInput(payload.encerramentolancamentos);
        const situacao = Number(payload.situacao);
        const versao = typeof periodo.versao === 'number' ? periodo.versao : 1;

        if (!normalizedDate || [1, 2].indexOf(situacao) === -1) {
            this.handleError(
                { message: 'Informe encerramento de movimentos e situação válidos.' },
                'Não foi possível atualizar o período.'
            );
            return this.$q.reject();
        }

        const updatePayload: IMovimentacaoPeriodoUpdatePayload = {
            situacao,
            encerramentolancamentos: normalizedDate,
            versao: typeof payload.versao === 'number' ? payload.versao : versao
        };

        return this.MovimentacoesPeriodosService.update(periodo.movimentacaoperiodo, updatePayload)
            .then(() => {
                return this.refreshPeriodosContext(periodo.movimentacaoperiodo)
                    .then(() => {
                        const updatedPeriodo = this.periodos.find(
                            (item) => item.movimentacaoperiodo === periodo.movimentacaoperiodo
                        );

                        if (!updatedPeriodo) {
                            return this.$q.reject({ message: 'Período atualizado não foi encontrado após atualização da listagem.' });
                        }

                        this.NewToaster.pop({
                            type: 'success',
                            title: 'Período atualizado com sucesso.'
                        });

                        return updatedPeriodo;
                    });
            })
            .catch((error) => {
                this.handleError(error, 'Não foi possível atualizar o período.');
                return this.$q.reject(error);
            });
    }

    public openTodosPeriodosModal(): void {
        if (!this.canManageMovimentacoes()) {
            return;
        }

        const modal = this.MovimentacoesPeriodosModalService.open(
            this.periodos,
            (payload) => this.submitNovoPeriodo(payload),
            ({ periodo, payload }) => this.submitEdicaoPeriodo(periodo, payload),
            () => this.buildNovoPeriodoForm(),
            (identifier) => this.MovimentacoesPeriodosService.get(identifier),
            () => this.canManageMovimentacoes(),
            () => this.canEditPeriodo()
        );
        modal.result.catch(() => {/* */ });
    }

    public hasSelection(): boolean {
        return !!this.selectedPeriod;
    }

    public isSelectedPeriodClosed(): boolean {
        return isMovimentacaoPeriodoEncerrado(this.selectedPeriod);
    }

    public canManageMovimentacoes(): boolean {
        return this.$rootScope.temPermissao('criar_movimentacao') === true;
    }

    public canEditPeriodo(): boolean {
        return this.$rootScope.temPermissao('meurhbureaus_membro') === true;
    }

    public isInitialLoading(): boolean {
        return this.periodosBusy && !this.hasSelection();
    }

    public loadPeriodos(ano: number = this.periodosAnoReferencia, targetPeriodoId?: string): angular.IPromise<IMovimentacaoPeriodo[]> {
        if (!this.estabelecimento) {
            this.handleError({ message: 'Estabelecimento não encontrado na sessão.' }, 'Não foi possível identificar o estabelecimento atual.');
            return this.$q.reject();
        }

        this.periodosAnoReferencia = ano;
        this.periodosBusy = true;

        return this.MovimentacoesPeriodosService.list({
            estabelecimento: this.estabelecimento,
            ano,
            order: { mes: 'DESC' }
        })
            .then((periodos) => {
                this.periodos = Array.isArray(periodos) ? periodos : [];
                const nextSelectedPeriod = resolvePeriodoSelection(this.periodos, targetPeriodoId, this.selectedPeriod);
                this.selectedPeriod = nextSelectedPeriod;
                this.quickAddOpen = this.quickAddOpen &&
                    this.canManageMovimentacoes() &&
                    !!nextSelectedPeriod &&
                    !isMovimentacaoPeriodoEncerrado(nextSelectedPeriod);

                if (!nextSelectedPeriod) {
                    this.quickAddOpen = false;
                    this.closeDetalhe(false);
                    this.syncRouteContext();
                    return [];
                }

                this.applyRouteDrawerState();
                this.syncRouteContext();
                return this.periodos;
            })
            .catch((error) => {
                this.periodos = [];
                this.selectedPeriod = null;
                this.closeDetalhe(false);
                this.handleError(error, 'Não foi possível carregar os períodos de movimentação.');
                return this.$q.reject(error);
            })
            .finally(() => {
                this.periodosBusy = false;
            });
    }

    public toggleQuickAdd(): void {
        if (!this.canManageMovimentacoes() || !this.selectedPeriod || this.quickAddBusy) {
            return;
        }

        if (this.isSelectedPeriodClosed()) {
            this.notifyPeriodoEncerrado();
            return;
        }

        if (this.quickAddOpen) {
            return;
        }

        this.quickAddOpen = true;
    }

    public closeQuickAdd(): void {
        this.quickAddOpen = false;
    }

    public openDetalhe(entity: IMovimentacaoListItem): void {
        if (!entity || !entity.movimentacao || !this.selectedPeriod) {
            return;
        }

        this.detalheAberto = true;
        this.detalheMovimentacaoId = entity.movimentacao;
        this.routeMovimentacaoId = entity.movimentacao;
        this.syncRouteContext();
    }

    public closeDetalhe(syncRoute: boolean = true): void {
        this.detalheAberto = false;
        this.detalheMovimentacaoId = null;
        this.routeMovimentacaoId = null;

        if (syncRoute) {
            this.syncRouteContext();
        }
    }

    public handleDetalheChanged(): angular.IPromise<void> {
        return this.refreshListagemContext();
    }

    public handleListagemChanged(): angular.IPromise<void> {
        return this.refreshListagemContext();
    }

    public submitQuickAdd(
        payload: IMovimentacaoPayload,
        mode: TMovimentacaoQuickAddSubmitMode
    ): angular.IPromise<void> {
        if (!this.canManageMovimentacoes()) {
            return this.$q.reject();
        }

        if (!this.selectedPeriod) {
            this.handleError({ message: 'Selecione um período para criar movimentações.' }, 'Não foi possível salvar a movimentação.');
            return this.$q.reject();
        }

        if (this.isSelectedPeriodClosed()) {
            this.notifyPeriodoEncerrado();
            return this.$q.reject({ message: 'Períodos encerrados não permitem alterações em movimentações.' });
        }

        this.quickAddBusy = true;
        const payloadWithPeriodo: IMovimentacaoPayload = {
            ...payload,
            movimentacaoperiodo: this.selectedPeriod.movimentacaoperiodo
        };

        return this.MovimentacoesService.create(payloadWithPeriodo)
            .then((response) => {
                if (mode === 'send') {
                    return this.MovimentacoesService.enviar(response.movimentacao).then(() => response.movimentacao);
                }

                return response.movimentacao;
            })
            .then(() => {
                return this.refreshListagemContext(
                    this.selectedPeriod!.movimentacaoperiodo,
                    mode === 'send' ? 'enviado' : 'rascunho'
                );
            })
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: mode === 'send'
                        ? 'Movimentação salva e enviada com sucesso.'
                        : 'Movimentação salva como rascunho.'
                });
            })
            .catch((error) => {
                this.handleError(error, 'Não foi possível salvar a movimentação.');
                return this.$q.reject(error);
            })
            .finally(() => {
                this.quickAddBusy = false;
            });
    }

    private handlePeriodoCreated(
        response: IMovimentacaoPeriodoCreateResponse,
        ano: number
    ): angular.IPromise<IMovimentacaoPeriodo> {
        if (!response || !response.movimentacaoperiodo) {
            return this.$q.reject({ message: 'A resposta da criação do período não retornou o identificador.' });
        }

        return this.loadPeriodos(ano, response.movimentacaoperiodo)
            .then(() => {
                const createdPeriodo = this.periodos.find(
                    (periodo) => periodo.movimentacaoperiodo === response.movimentacaoperiodo
                );

                if (!createdPeriodo) {
                    return this.$q.reject({ message: 'Período criado não foi encontrado após atualização da listagem.' });
                }

                return createdPeriodo;
            });
    }

    private refreshPeriodosContext(targetPeriodoId?: string | null): angular.IPromise<IMovimentacaoPeriodo[]> {
        const periodoId = targetPeriodoId || this.selectedPeriod?.movimentacaoperiodo;
        return this.loadPeriodos(this.periodosAnoReferencia, periodoId || undefined);
    }

    private refreshListagemContext(
        targetPeriodoId?: string | null,
        preferredStatusValue: IMovimentacaoListRefreshRequest['preferredStatusValue'] = null
    ): angular.IPromise<void> {
        return this.refreshPeriodosContext(targetPeriodoId)
            .then(() => {
                this.bumpListRefreshRequest(preferredStatusValue);
                return undefined;
            });
    }

    private notifyPeriodoEncerrado(): void {
        this.NewToaster.pop({
            type: 'error',
            title: 'Período encerrado.',
            body: 'Períodos encerrados não permitem alterações em movimentações.'
        });
    }

    private loadWorkflowContext(): void {
        this.MovimentacoesWorkflowService.getWorkflowStatus()
            .then((response) => {
                this.workflowEnabled = !!response?.ativado;
            })
            .catch(() => {
                this.workflowEnabled = false;
            });
    }

    private normalizePeriodoPayload(payload: IMovimentacaoPeriodoForm): IMovimentacaoPeriodoPayload | null {
        if (!payload || !payload.encerramentolancamentos) {
            return null;
        }

        const mes = Number(payload.mes);
        const ano = Number(payload.ano);
        if (!Number.isFinite(mes) || !Number.isFinite(ano)) {
            return null;
        }

        const formattedDate = normalizePeriodoDateInput(payload.encerramentolancamentos);
        if (!formattedDate) {
            return null;
        }

        return {
            estabelecimento: this.estabelecimento,
            mes,
            ano,
            situacao: 1,
            encerramentolancamentos: formattedDate
        };
    }

    private handleError(error: any, fallbackMessage: string): void {
        const message = extractMovimentacaoErrorMessage(error) || fallbackMessage;

        this.NewToaster.pop({
            type: 'error',
            title: fallbackMessage,
            body: message !== fallbackMessage ? message : undefined
        });
    }

    private applyRouteDrawerState(): void {
        if (!this.selectedPeriod || !this.routeMovimentacaoId) {
            this.closeDetalhe(false);
            return;
        }

        this.detalheMovimentacaoId = this.routeMovimentacaoId;
        this.detalheAberto = true;
    }

    private syncRouteContext(): void {
        this.routePeriodoId = this.selectedPeriod ? this.selectedPeriod.movimentacaoperiodo : null;
        this.routeMovimentacaoId = this.detalheAberto ? this.detalheMovimentacaoId : null;

        this.$state.go('movimentacoes', {
            periodo: this.routePeriodoId,
            movimentacao: this.routeMovimentacaoId
        }, {
            notify: false,
            location: 'replace',
            inherit: false
        });
    }

    private readStateParam(param: string): string | null {
        const value = this.$stateParams[param];
        return typeof value === 'string' && value.trim()
            ? value.trim()
            : null;
    }

    private bumpListRefreshRequest(preferredStatusValue: IMovimentacaoListRefreshRequest['preferredStatusValue'] = null): void {
        this.listRefreshRequest = {
            token: this.listRefreshRequest.token + 1,
            preferredStatusValue
        };
    }

}
